import { useMemo, useState } from 'react';

import type { ApiWorkHistoryPositionType } from '#/api/types/ApiWorkHistoryPositionType';
import { ExpandButton } from '#/components/ExpandButton/ExpandButton';
import { Stack } from '#/components/Stack/Stack';
import { Tabs } from '#/components/Tabs/Tabs';
import { createTab } from '#/components/Tabs/createTab';
import { useShowMore } from '#/hooks/useShowMore';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { UserProfileApiApplicantProfile } from '#/modules/userProfile/api/types/UserProfileApiApplicantProfile';
import { ViewUserProfilePageContentSection } from '#/modules/userProfile/components/ViewUserProfilePageContentSection';
import { ViewUserProfileWorkHistoryJobItem } from '#/modules/userProfile/components/WorkHistoryJobItem/ViewUserProfileWorkHistoryJobItem';
import { useUserProfileWorkHistoryJobItems } from '#/modules/userProfile/hooks/useUserProfileWorkHistoryJobItems';
import { spacing } from '#/theme/theme';

type Props = {
  applicantProfile: UserProfileApiApplicantProfile;
};

export function ViewUserProfilePageContentSectionProfessionalExperience({
  applicantProfile,
}: Props) {
  const { i18n } = useI18n();

  const { workHistoryByPositionType, meta } = useUserProfileWorkHistoryJobItems(
    applicantProfile.workHistoryJobs || [],
  );

  const initialWorkHistoryType = useMemo(() => {
    if (meta.hasJobs) return 'JOB';
    if (!meta.hasJobs && meta.hasInternships) return 'INTERNSHIP';
    if (!meta.hasJobs && !meta.hasInternships && meta.hasVolops) return 'VOLOP';
    return 'JOB';
  }, [meta]);

  const [workHistoryType, setWorkHistoryType] =
    useState<ApiWorkHistoryPositionType>(initialWorkHistoryType);

  const { canShowMore, showMore, toggleShowMore, visibleItems } = useShowMore(
    workHistoryByPositionType[workHistoryType] ?? [],
    3,
  );

  const qaId = 'view-user-profile-page-content-section-professional-experience';

  const workHistoryItemTabs = [
    ...(meta.hasJobs && !meta.hasInternships && !meta.hasVolops
      ? [
          createTab({
            prefix: qaId,
            value: 'JOB',
            label: i18n('Employment'),
            qaId: 'tab',
          }),
        ]
      : []),
    ...(meta.hasJobs && (meta.hasInternships || meta.hasVolops)
      ? [
          createTab({
            prefix: qaId,
            value: 'JOB',
            label: i18n('Jobs'),
            qaId: 'tab',
          }),
        ]
      : []),
    ...(meta.hasInternships
      ? [
          createTab({
            prefix: qaId,
            value: 'INTERNSHIP',
            label: i18n('Internships'),
            qaId: 'tab',
          }),
        ]
      : []),
    ...(meta.hasVolops
      ? [
          createTab({
            prefix: qaId,
            value: 'VOLOP',
            label: i18n('Volunteering'),
            qaId: 'tab',
          }),
        ]
      : []),
  ] as const;
  return (
    <ViewUserProfilePageContentSection
      qaId={qaId}
      title={i18n('Professional Experience')}
    >
      <Stack direction="column" spacing={spacing[40]}>
        <Tabs
          tabs={workHistoryItemTabs}
          value={workHistoryType}
          onSelectTab={setWorkHistoryType}
          variant="compact-marker"
          layout="horizontal"
        />
        <Stack direction="column" spacing={spacing[40]}>
          {visibleItems.map((workHistoryJob) => (
            <ViewUserProfileWorkHistoryJobItem
              key={workHistoryJob.id}
              workHistoryJob={workHistoryJob}
            />
          ))}
        </Stack>
        {canShowMore ? (
          <ExpandButton onClick={toggleShowMore} open={showMore}>
            {showMore ? i18n('View less') : i18n('View more')}
          </ExpandButton>
        ) : null}
      </Stack>
    </ViewUserProfilePageContentSection>
  );
}
